import React, { Component } from 'react';

/**
 * ErrorBoundary - React error boundary component (SAT-96)
 *
 * Catches JavaScript errors in child components, logs them,
 * and displays a fallback UI instead of crashing the whole app.
 *
 * Features:
 * - Catches rendering errors in child components
 * - Shows friendly error message with recovery options
 * - Logs errors for debugging
 * - Allows retry/recovery
 * - Supports custom fallback UI
 *
 * Usage:
 * <ErrorBoundary fallback={<CustomFallback />}>
 *   <YourComponent />
 * </ErrorBoundary>
 *
 * Or with automatic fallback:
 * <ErrorBoundary level="page">
 *   <YourPage />
 * </ErrorBoundary>
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({ errorInfo });

    // You could also log to an error reporting service here
    // e.g., Sentry, LogRocket, etc.
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState((prevState) => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    const { hasError, error, errorInfo, retryCount } = this.state;
    const { children, fallback, level = 'component' } = this.props;

    if (hasError) {
      // Custom fallback provided
      if (fallback) {
        return typeof fallback === 'function'
          ? fallback({ error, errorInfo, retry: this.handleRetry })
          : fallback;
      }

      // Default fallback based on level
      return (
        <ErrorFallback
          level={level}
          error={error}
          errorInfo={errorInfo}
          retryCount={retryCount}
          onRetry={this.handleRetry}
          onReload={this.handleReload}
          onGoHome={this.handleGoHome}
        />
      );
    }

    return children;
  }
}

/**
 * ErrorFallback - Default fallback UI for error boundaries
 */
function ErrorFallback({
  level,
  error,
  errorInfo,
  retryCount,
  onRetry,
  onReload,
  onGoHome
}) {
  const isDev = process.env.NODE_ENV === 'development';
  const maxRetries = 3;
  const canRetry = retryCount < maxRetries;

  // Compact fallback for component-level errors
  if (level === 'component') {
    return (
      <div
        className="p-4 rounded-lg border border-red-200 dark:border-red-800
          bg-red-50 dark:bg-red-900/20 animate-fadeIn"
        role="alert"
      >
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-8 h-8 rounded-full bg-red-100 dark:bg-red-800/30
            flex items-center justify-center">
            <svg
              className="w-4 h-4 text-red-600 dark:text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium text-red-800 dark:text-red-300">
              Something went wrong
            </h4>
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">
              This component couldn't load properly.
            </p>
            {canRetry && (
              <button
                onClick={onRetry}
                className="mt-2 text-sm font-medium text-red-700 dark:text-red-300
                  hover:text-red-800 dark:hover:text-red-200 underline"
              >
                Try again
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Full-page fallback for section/page-level errors
  return (
    <div
      className="min-h-[400px] flex items-center justify-center p-8 animate-fadeIn"
      role="alert"
    >
      <div className="max-w-md w-full text-center">
        {/* Error icon */}
        <div className="mx-auto w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30
          flex items-center justify-center mb-6">
          <svg
            className="w-8 h-8 text-red-600 dark:text-red-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>

        {/* Error message */}
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          Oops! Something went wrong
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          {level === 'page'
            ? "We're having trouble loading this page. Please try again."
            : "This section couldn't load properly. You can try again or continue using other parts of the app."}
        </p>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {canRetry && (
            <button
              onClick={onRetry}
              className="inline-flex items-center justify-center gap-2 px-4 py-2
                bg-decisiv-blue text-white font-medium rounded-lg
                hover:bg-decisiv-blue/90 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Try Again
            </button>
          )}

          <button
            onClick={onReload}
            className="inline-flex items-center justify-center gap-2 px-4 py-2
              bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-lg
              hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Reload Page
          </button>

          {level === 'section' && (
            <button
              onClick={onGoHome}
              className="inline-flex items-center justify-center gap-2 px-4 py-2
                text-gray-600 dark:text-gray-400 font-medium
                hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                />
              </svg>
              Go Home
            </button>
          )}
        </div>

        {/* Retry count indicator */}
        {retryCount > 0 && (
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            Retry attempt {retryCount} of {maxRetries}
          </p>
        )}

        {/* Developer error details */}
        {isDev && error && (
          <details className="mt-6 text-left">
            <summary className="cursor-pointer text-sm text-gray-500 dark:text-gray-400
              hover:text-gray-700 dark:hover:text-gray-200">
              View error details (dev only)
            </summary>
            <div className="mt-2 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-auto">
              <p className="text-sm font-mono text-red-600 dark:text-red-400 break-all">
                {error.toString()}
              </p>
              {errorInfo?.componentStack && (
                <pre className="mt-2 text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                  {errorInfo.componentStack}
                </pre>
              )}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}

/**
 * withErrorBoundary - HOC to wrap a component with an error boundary
 */
export function withErrorBoundary(WrappedComponent, options = {}) {
  const { level = 'component', fallback, onError } = options;

  function WithErrorBoundary(props) {
    return (
      <ErrorBoundary level={level} fallback={fallback} onError={onError}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  }

  WithErrorBoundary.displayName = `withErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return WithErrorBoundary;
}

export default ErrorBoundary;
